import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Download, Loader2, Plus, Trash2, UploadCloud } from 'lucide-react'

import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import apiClient from '@/shared/lib/apiClient'
import { t, type TranslationKey } from '@/shared/lib/i18n'
import {
  adminInstrumentTypeAttachmentPath,
  adminInstrumentTypeAttachmentsPath,
} from '@/shared/lib/routes'
import type { WebAdminInstrumentTypeAttachment } from '@/shared/types/web'

// ── Types ─────────────────────────────────────────────────────────────────────

type PendingUpload = {
  localId: string
  file: File
  description: string
}

export type CommitError = {
  localId: string
  fileName: string
  message: string
}

export type CommitResult = {
  errors: CommitError[]
}

export type InstrumentTypeAttachmentsManagerHandle = {
  /**
   * Attempts to upload all queued files and apply all queued deletes.
   * Returns a result object — never throws. Check `errors` before proceeding.
   */
  commitStagedChanges: () => Promise<CommitResult>
}

type InstrumentTypeAttachmentsManagerProps = {
  instrumentTypeId: number
  canCreate: boolean
  canDelete: boolean
  onChanged?: (attachmentCount: number) => void
  /**
   * When true, uploads and deletes are queued locally rather than sent
   * immediately. Call `commitStagedChanges()` via ref to apply them.
   * Defaults to false (immediate mode).
   */
  staged?: boolean
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fileExtensionBadge = (name: string) => name.split('.').pop()?.toUpperCase() ?? '—'

// ── Component ─────────────────────────────────────────────────────────────────

export const InstrumentTypeAttachmentsManager = forwardRef<
  InstrumentTypeAttachmentsManagerHandle,
  InstrumentTypeAttachmentsManagerProps
>(({ instrumentTypeId, canCreate, canDelete, onChanged, staged = false }, ref) => {
  const immediateFileInputRef = useRef<HTMLInputElement>(null)
  const stagedFileInputRef = useRef<HTMLInputElement>(null)

  const [attachments, setAttachments] = useState<WebAdminInstrumentTypeAttachment[]>([])
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  // Tracks which instrumentTypeId the current fetch state belongs to.
  // When it differs from the prop, we reset loading state during render
  // (the React-recommended alternative to synchronous setState in effects).
  const [fetchedForId, setFetchedForId] = useState<number | null>(null)

  if (fetchedForId !== instrumentTypeId) {
    setFetchedForId(instrumentTypeId)
    setLoading(true)
    setLoadFailed(false)
  }

  // Immediate-mode state
  const [uploading, setUploading] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [description, setDescription] = useState('')

  // Staged-mode state
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([])
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<number>>(new Set())
  const [uploadErrors, setUploadErrors] = useState<Record<string, string>>({})

  // ── Fetch on mount ──────────────────────────────────────────────────────────

  useEffect(() => {
    let active = true
    apiClient
      .get<{ attachments: WebAdminInstrumentTypeAttachment[] }>(
        adminInstrumentTypeAttachmentsPath(instrumentTypeId),
      )
      .then((response) => {
        if (active) setAttachments(response.data.attachments)
      })
      .catch(() => {
        if (active) setLoadFailed(true)
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [instrumentTypeId])

  // ── Imperative handle (staged mode only) ────────────────────────────────────

  useImperativeHandle(ref, () => ({
    commitStagedChanges: async (): Promise<CommitResult> => {
      const errors: CommitError[] = []
      const newUploadErrors: Record<string, string> = {}
      const succeededLocalIds: string[] = []
      let latestAttachments: WebAdminInstrumentTypeAttachment[] | null = null

      // Upload sequentially so each failure is attributed to the correct file
      for (const { localId, file, description: desc } of pendingUploads) {
        const formData = new FormData()
        formData.append('file', file)
        if (desc.trim()) formData.append('attachment_description', desc.trim())
        try {
          const response = await apiClient.post<{
            attachments: WebAdminInstrumentTypeAttachment[]
          }>(adminInstrumentTypeAttachmentsPath(instrumentTypeId), formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
          succeededLocalIds.push(localId)
          latestAttachments = response.data.attachments
        } catch (error: unknown) {
          const serverMessage = (error as { response?: { data?: { error?: unknown } } })?.response
            ?.data?.error
          const message =
            typeof serverMessage === 'string'
              ? serverMessage
              : t('admin.instrumentTypes.attachments.uploadFailed')
          errors.push({ localId, fileName: file.name, message })
          newUploadErrors[localId] = message
        }
      }

      // Reflect saved files in the list and remove them from the pending queue
      if (latestAttachments) setAttachments(latestAttachments)
      if (succeededLocalIds.length > 0) {
        setPendingUploads((prev) => prev.filter((p) => !succeededLocalIds.includes(p.localId)))
      }

      // Only run deletes if no upload errors — keep state consistent
      if (errors.length === 0) {
        await Promise.all(
          Array.from(pendingDeleteIds).map((id) =>
            apiClient.delete(adminInstrumentTypeAttachmentPath(instrumentTypeId, id)),
          ),
        )
      }

      setUploadErrors(newUploadErrors)
      return { errors }
    },
  }))

  // ── Immediate-mode helpers ───────────────────────────────────────────────────

  const applyAttachments = (next: WebAdminInstrumentTypeAttachment[]) => {
    setAttachments(next)
    onChanged?.(next.length)
  }

  const resetPendingFile = () => {
    setPendingFile(null)
    if (immediateFileInputRef.current) immediateFileInputRef.current.value = ''
  }

  const handleUploadImmediate = (event: React.FormEvent) => {
    event.preventDefault()
    if (!pendingFile) return

    setUploading(true)
    setErrorMessage(null)

    const formData = new FormData()
    formData.append('file', pendingFile)
    if (description.trim()) formData.append('attachment_description', description.trim())

    apiClient
      .post<{ attachments: WebAdminInstrumentTypeAttachment[] }>(
        adminInstrumentTypeAttachmentsPath(instrumentTypeId),
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then((response) => {
        applyAttachments(response.data.attachments)
        resetPendingFile()
        setDescription('')
      })
      .catch((error) => {
        const message = error?.response?.data?.error
        setErrorMessage(
          typeof message === 'string'
            ? message
            : t('admin.instrumentTypes.attachments.uploadFailed'),
        )
      })
      .finally(() => setUploading(false))
  }

  const handleDeleteImmediate = (attachmentId: number) => {
    setDeletingId(attachmentId)
    setErrorMessage(null)
    apiClient
      .delete<{ attachments: WebAdminInstrumentTypeAttachment[] }>(
        adminInstrumentTypeAttachmentPath(instrumentTypeId, attachmentId),
      )
      .then((response) => applyAttachments(response.data.attachments))
      .catch(() => setErrorMessage(t('admin.instrumentTypes.attachments.deleteFailed')))
      .finally(() => setDeletingId(null))
  }

  // ── Staged-mode handlers ─────────────────────────────────────────────────────

  const handleStagedFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files?.length) return
    const newPending: PendingUpload[] = Array.from(files).map((file) => ({
      localId: crypto.randomUUID(),
      file,
      description: '',
    }))
    setPendingUploads((prev) => [...prev, ...newPending])
    if (stagedFileInputRef.current) stagedFileInputRef.current.value = ''
  }

  const updatePendingDescription = (localId: string, value: string) => {
    setPendingUploads((prev) =>
      prev.map((p) => (p.localId === localId ? { ...p, description: value } : p)),
    )
  }

  const removePendingUpload = (localId: string) => {
    setPendingUploads((prev) => prev.filter((p) => p.localId !== localId))
    setUploadErrors((prev) => {
      const next = { ...prev }
      delete next[localId]
      return next
    })
  }

  const handleStagedDelete = (attachmentId: number) => {
    setPendingDeleteIds((prev) => new Set([...prev, attachmentId]))
  }

  // ── Derived ──────────────────────────────────────────────────────────────────

  const visibleAttachments = staged
    ? attachments.filter((a) => !pendingDeleteIds.has(a.id))
    : attachments

  const isEmpty = visibleAttachments.length === 0 && pendingUploads.length === 0

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {errorMessage && (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          role="alert"
        >
          {errorMessage}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : loadFailed ? (
        <p className="py-4 text-center text-sm text-destructive">
          {t('admin.instrumentTypes.attachments.loadFailed')}
        </p>
      ) : isEmpty ? (
        <p className="text-sm text-muted-foreground">
          {t('admin.instrumentTypes.attachments.empty')}
        </p>
      ) : (
        <ul className="divide-y rounded-md border">
          {visibleAttachments.map((attachment) => (
            <li key={attachment.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
              <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                {fileExtensionBadge(attachment.attachmentName ?? '')}
              </span>

              <div className="min-w-0 flex-1">
                <a
                  href={attachment.downloadHref}
                  className="block truncate text-sm font-medium text-primary underline-offset-2 hover:underline"
                >
                  {attachment.attachmentName}
                </a>
                {attachment.attachmentDescription && (
                  <p className="truncate text-xs text-muted-foreground">
                    {attachment.attachmentDescription}
                  </p>
                )}
              </div>

              <div className="flex shrink-0 items-center gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                  <a href={attachment.downloadHref} download>
                    <Download className="h-3.5 w-3.5" />
                    <span className="sr-only">
                      {t('admin.instrumentTypes.attachments.downloadSr')}
                    </span>
                  </a>
                </Button>

                {canDelete && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    disabled={!staged && deletingId === attachment.id}
                    onClick={() =>
                      staged
                        ? handleStagedDelete(attachment.id)
                        : handleDeleteImmediate(attachment.id)
                    }
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    <span className="sr-only">
                      {t('admin.instrumentTypes.attachments.deleteSr')}
                    </span>
                  </Button>
                )}
              </div>
            </li>
          ))}

          {/* Pending uploads — staged mode only */}
          {pendingUploads.map((pending) => {
            const uploadError = uploadErrors[pending.localId]
            return (
              <li
                key={pending.localId}
                className={`flex flex-wrap items-center gap-3 px-3 py-2.5 ${uploadError ? 'bg-destructive/5' : 'bg-muted/30'}`}
              >
                <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                  {fileExtensionBadge(pending.file.name)}
                </span>

                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="min-w-0 max-w-[180px] shrink-0 truncate text-sm font-medium">
                      {pending.file.name}
                    </span>
                    <Input
                      value={pending.description}
                      onChange={(event) =>
                        updatePendingDescription(pending.localId, event.target.value)
                      }
                      placeholder={t('admin.instrumentTypes.attachments.descriptionPlaceholder')}
                      className="h-8 text-sm"
                    />
                  </div>
                  {uploadError && <p className="text-xs text-destructive">{uploadError}</p>}
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0 text-destructive hover:text-destructive"
                  onClick={() => removePendingUpload(pending.localId)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  <span className="sr-only">
                    {t('admin.instrumentTypes.attachments.removePendingSr' as TranslationKey)}
                  </span>
                </Button>
              </li>
            )
          })}
        </ul>
      )}

      {/* ── Staged mode: simple "+ Add file" button ── */}
      {staged && canCreate && (
        <div className="flex flex-wrap items-center gap-3 border-t pt-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => stagedFileInputRef.current?.click()}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            {t('admin.instrumentTypes.attachments.addFile' as TranslationKey)}
          </Button>
          <input
            ref={stagedFileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleStagedFileSelect}
          />
        </div>
      )}

      {/* ── Immediate mode: full upload form ── */}
      {!staged && canCreate && (
        <form onSubmit={handleUploadImmediate} className="space-y-4 border-t pt-4">
          <div className="space-y-1.5">
            <Label htmlFor="instrument-type-attachment-file">
              {t('admin.instrumentTypes.attachments.fileUpload')}
            </Label>
            <label
              htmlFor="instrument-type-attachment-file"
              className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/25 px-4 py-5 text-center transition-colors hover:bg-muted/50"
            >
              <UploadCloud className="h-6 w-6 text-muted-foreground" />
              <div className="text-sm">
                {pendingFile ? (
                  <span className="font-medium text-foreground">{pendingFile.name}</span>
                ) : (
                  <>
                    <span className="font-medium text-primary">
                      {t('admin.instrumentTypes.attachments.chooseFile')}
                    </span>
                    <span className="text-muted-foreground">
                      {' '}
                      {t('admin.instrumentTypes.attachments.orDragDrop')}
                    </span>
                  </>
                )}
              </div>
              <span className="text-xs text-muted-foreground">
                {t('admin.instrumentTypes.attachments.allowedTypes')}
              </span>
              <Input
                id="instrument-type-attachment-file"
                type="file"
                ref={immediateFileInputRef}
                className="sr-only"
                onChange={(event) => setPendingFile(event.target.files?.[0] ?? null)}
              />
            </label>
            {pendingFile && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                  {fileExtensionBadge(pendingFile.name)}
                </span>
                <Button type="button" variant="ghost" size="sm" onClick={resetPendingFile}>
                  <Trash2 className="mr-1 h-3.5 w-3.5" />
                  {t('admin.instrumentTypes.attachments.clearFile')}
                </Button>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="instrument-type-attachment-description">
              {t('admin.instrumentTypes.attachments.descriptionLabel')}
            </Label>
            <Input
              id="instrument-type-attachment-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={t('admin.instrumentTypes.attachments.descriptionPlaceholder')}
            />
          </div>

          <Button type="submit" size="sm" disabled={uploading || !pendingFile}>
            <UploadCloud className="mr-1.5 h-3.5 w-3.5" />
            {uploading
              ? t('admin.instrumentTypes.attachments.uploading')
              : t('admin.instrumentTypes.attachments.upload')}
          </Button>
        </form>
      )}
    </div>
  )
})

InstrumentTypeAttachmentsManager.displayName = 'InstrumentTypeAttachmentsManager'

export default InstrumentTypeAttachmentsManager
